/**
 * TomlAbilityLoader — pure helper for `AbilityBuilder.withTomlData()`.
 *
 * Reads the TOML-generated AbilityDataDef for an ability and returns a patch
 * that AbilityBuilder merges into its metadata (and optionally its
 * windUpDuration). Extracted from AbilityBuilder.ts so the builder can stay
 * focused on fluent orchestration.
 *
 * Follows the same pattern as AbilityBorrowing.ts: pure function, no `this`
 * state, no builder coupling — unit-testable without instantiating a builder.
 */

import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';
import { gameLogger } from '../../../../utils/GameLogger';

/**
 * Patch returned by {@link loadTomlAbilityData}. The builder applies this via
 * `Object.assign(this.metadata, patch.metadata)` + an optional
 * `this.windUpDuration` write. Metadata is typed as `Record<string, unknown>`
 * because several fields (`baseDamage`, `scaleFactor`, etc.) live outside
 * the formal `AbilityMetadata` type — they're read by the damage formula at
 * cast time via `(metadata as any).baseDamage`.
 */
export interface TomlAbilityPatch {
  metadata: Record<string, unknown>;
  windUpDuration?: number;
}

/**
 * Load balance data for `abilityId` from the TOML-generated ability table.
 * Returns null + logs a warning if the ID has no registered data.
 *
 * Callers: `AbilityBuilder.withTomlData()`. The builder then merges the
 * returned patch into its own state.
 */
export function loadTomlAbilityData(abilityId: string, ownerAbilityId = abilityId): TomlAbilityPatch | null {
  const data = getAbilityData(abilityId);
  if (!data) {
    gameLogger.warn('abilities', `loadTomlAbilityData: no TOML data for "${abilityId}"`);
    return null;
  }

  const metadata: Record<string, unknown> = {
    manaCost: data.manaCost,
    cooldown: data.cooldownMs,
  };
  const owner = getAbilityData(ownerAbilityId);
  if (owner?.range !== undefined) metadata.range = owner.range;
  let windUpDuration: number | undefined;

  if (data.windUpMs) {
    windUpDuration = data.windUpMs;
    metadata.castTime = data.windUpMs;
  }

  // Plan 29 F2: the TOML description is the ability's text everywhere (it
  // outranks a module's hand-written withDescription, which drifted).
  if (data.description) metadata.description = data.description;
  // Damage scaling (read by the damage formula at cast time)
  if (data.baseDamage !== undefined) metadata.baseDamage = data.baseDamage;
  if (data.scaleFactor !== undefined) metadata.scaleFactor = data.scaleFactor;
  if (data.durationMs) metadata.duration = data.durationMs;
  if (data.radius) metadata.radius = data.radius;
  if (data.slowValue !== undefined) metadata.slowValue = data.slowValue;
  if (data.slowDurationMs) metadata.slowDurationMs = data.slowDurationMs;
  if (data.defenseBonus) metadata.defenseBonus = data.defenseBonus;
  if (data.attackBonus) metadata.attackBonus = data.attackBonus;

  // Classification fields (school, type, mechanic, target)
  if (data.school) metadata.school = data.school;
  if (data.class) metadata.class = data.class;
  if (data.abilityType) metadata.abilityType = data.abilityType;

  // VFX from TOML (consumed by VFX effect registration at build time)
  if (data.vfx) metadata.tomlVfx = data.vfx;

  return { metadata, windUpDuration };
}
